import { getGatewayBaseUrl } from "../config";
import type { CustomerOpenReceivableRuntimeItem } from "../gateway/local-projection-client";
import {
  PosReceivableSettlementAllGatewayClient,
  type ReceivableSettlementAllItem,
  type ReceivableSettlementAllResult,
} from "../gateway/receivable-settlement-all-client";
import type { CashShiftSnapshot, EmployeeRuntimeActor } from "../gateway/identity-client";
import { clearSettledCustomerMemberContext } from "./member-context-lifecycle";
import { applyCanonicalCustomerCreditSummaryReadback } from "./receivable-credit-readback";
import { posRuntimeDb, type JournalRecord, type OutboxRecord } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

export type LocalReceivableSettlementAllSnapshot = {
  schema: "LOCAL_RECEIVABLE_SETTLEMENT_ALL_V1";
  localBatchId: string;
  rootRequestKey: string;
  organizationId: string;
  collectionBranchId: string;
  nodeId: string;
  performingEmployeeId: string;
  shiftId: string;
  customerId: string;
  receivables: ReceivableSettlementAllItem[];
  expectedTotal: number;
  currencyCode: string;
  paymentMethodCode: string;
  settlementMode: string;
  receivingDestinationId: string | null;
  receivingDestinationName: string | null;
  verificationSource: "CASH_TENDERED" | "STAFF_CONFIRMED";
  tenderedAmount: number | null;
  localChangeAmount: number | null;
  localCommittedAt: string;
  canonical: ReceivableSettlementAllResult | null;
};

export type CommitLocalReceivableSettlementAllInput = {
  identity: TrustedTerminalIdentity;
  actor: EmployeeRuntimeActor;
  shift: CashShiftSnapshot;
  customerId: string;
  receivables: CustomerOpenReceivableRuntimeItem[];
  paymentMethodCode: string;
  settlementMode: string;
  receivingDestinationId: string | null;
  receivingDestinationName: string | null;
  tenderedAmount: number | null;
  localChangeAmount: number | null;
};

type ReceivableSettlementAllOutboxPayload = {
  customerId: string;
  receivables: ReceivableSettlementAllItem[];
  expectedTotal: number;
  paymentMethodCode: string;
  receivingDestinationId: string | null;
  tenderedAmount: number | null;
  localChangeAmount: number | null;
  verificationSource: "CASH_TENDERED" | "STAFF_CONFIRMED";
  localCommittedAt: string;
};

function randomId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function settlementScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function batchLocalId(id: string) {
  return `receivable-settlement-all:${id}`;
}

function snapshotFromJournal(record: JournalRecord) {
  const snapshot = record.snapshot as LocalReceivableSettlementAllSnapshot;
  return snapshot?.schema === "LOCAL_RECEIVABLE_SETTLEMENT_ALL_V1" ? snapshot : null;
}

function remainingOf(item: CustomerOpenReceivableRuntimeItem) {
  const value = Number(item.remaining_amount ?? item.original_amount);
  return Number.isFinite(value) ? Math.max(value, 0) : 0;
}

async function pendingBatchForCustomer(identity: TrustedTerminalIdentity, customerId: string) {
  const scopeKey = settlementScope(identity);
  const rows = await posRuntimeDb.journal
    .where("scopeKey")
    .equals(scopeKey)
    .filter((record) => record.kind === "RECEIVABLE_SETTLEMENT_ALL" && !["CONFIRMED", "REJECTED"].includes(record.state))
    .toArray();
  return rows.find((record) => snapshotFromJournal(record)?.customerId === customerId) ?? null;
}

function normalizeReviewedReceivables(items: CustomerOpenReceivableRuntimeItem[]) {
  const seen = new Set<string>();
  const currencies = new Set<string>();
  const receivables: ReceivableSettlementAllItem[] = [];
  let expectedTotal = 0;

  for (const item of items) {
    if (!item.receivable_id || seen.has(item.receivable_id)) throw new Error("RECEIVABLE_BATCH_INVALID");
    const remainingAmount = remainingOf(item);
    if (remainingAmount <= 0) throw new Error("RECEIVABLE_BATCH_CHANGED");
    const currencyCode = (item.currency_code || "THB").trim().toUpperCase();
    currencies.add(currencyCode);
    seen.add(item.receivable_id);
    receivables.push({ receivable_id: item.receivable_id, remaining_amount: remainingAmount });
    expectedTotal += remainingAmount;
  }

  if (!receivables.length || receivables.length > 100) throw new Error("RECEIVABLE_BATCH_INVALID");
  if (currencies.size !== 1) throw new Error("RECEIVABLE_BATCH_CURRENCY_MISMATCH");
  expectedTotal = Math.round((expectedTotal + Number.EPSILON) * 100) / 100;
  if (!Number.isFinite(expectedTotal) || expectedTotal <= 0) throw new Error("RECEIVABLE_BATCH_TOTAL_INVALID");
  return { receivables, expectedTotal, currencyCode: [...currencies][0] };
}

export async function commitLocalReceivableSettlementAll(input: CommitLocalReceivableSettlementAllInput) {
  if (!input.actor.permission_codes.includes("commerce.sale.commit")) throw new Error("COMMERCE_SALE_COMMIT_PERMISSION_REQUIRED");
  if (input.shift.shift_status !== "OPEN") throw new Error("ACTIVE_POS_SHIFT_REQUIRED");
  if (!input.paymentMethodCode.trim()) throw new Error("PAYMENT_METHOD_CODE_REQUIRED");

  const existing = await pendingBatchForCustomer(input.identity, input.customerId);
  if (existing) {
    const snapshot = snapshotFromJournal(existing);
    if (!snapshot) throw new Error("RECEIVABLE_SETTLEMENT_ALL_PENDING_INTENT_INVALID");
    return snapshot;
  }

  const reviewed = normalizeReviewedReceivables(input.receivables);
  const settlementMode = input.settlementMode.trim().toUpperCase();
  const isCash = settlementMode === "IMMEDIATE_CASH";
  if (!isCash && settlementMode !== "RECEIVING_DESTINATION") throw new Error("PAYMENT_SETTLEMENT_MODE_NOT_SUPPORTED_V1");

  if (isCash) {
    if (input.receivingDestinationId) throw new Error("CASH_RECEIVING_DESTINATION_NOT_ALLOWED");
    if (input.tenderedAmount == null || input.tenderedAmount < reviewed.expectedTotal) throw new Error("TENDERED_AMOUNT_INVALID");
    const expectedChange = input.tenderedAmount - reviewed.expectedTotal;
    if (input.localChangeAmount == null || Math.abs(input.localChangeAmount - expectedChange) > 0.005) {
      throw new Error("LOCAL_CHANGE_AMOUNT_MISMATCH");
    }
  } else if (!input.receivingDestinationId) {
    throw new Error("PAYMENT_RECEIVING_DESTINATION_REQUIRED");
  }

  const localBatchId = randomId();
  const rootRequestKey = `pos-receivable-settlement-all-v1:${randomId()}`;
  const localCommittedAt = new Date().toISOString();
  const verificationSource = isCash ? "CASH_TENDERED" : "STAFF_CONFIRMED";
  const snapshot: LocalReceivableSettlementAllSnapshot = {
    schema: "LOCAL_RECEIVABLE_SETTLEMENT_ALL_V1",
    localBatchId,
    rootRequestKey,
    organizationId: input.identity.organization_id,
    collectionBranchId: input.identity.branch_id,
    nodeId: input.identity.node_id,
    performingEmployeeId: input.actor.employee_id,
    shiftId: input.shift.shift_id,
    customerId: input.customerId,
    receivables: reviewed.receivables,
    expectedTotal: reviewed.expectedTotal,
    currencyCode: reviewed.currencyCode,
    paymentMethodCode: input.paymentMethodCode.trim().toUpperCase(),
    settlementMode,
    receivingDestinationId: isCash ? null : input.receivingDestinationId,
    receivingDestinationName: isCash ? null : input.receivingDestinationName,
    verificationSource,
    tenderedAmount: isCash ? input.tenderedAmount : null,
    localChangeAmount: isCash ? input.localChangeAmount : null,
    localCommittedAt,
    canonical: null,
  };
  const payload: ReceivableSettlementAllOutboxPayload = {
    customerId: snapshot.customerId,
    receivables: snapshot.receivables,
    expectedTotal: snapshot.expectedTotal,
    paymentMethodCode: snapshot.paymentMethodCode,
    receivingDestinationId: snapshot.receivingDestinationId,
    tenderedAmount: snapshot.tenderedAmount,
    localChangeAmount: snapshot.localChangeAmount,
    verificationSource: snapshot.verificationSource,
    localCommittedAt,
  };
  const scopeKey = settlementScope(input.identity);
  const outbox: OutboxRecord = {
    requestKey: rootRequestKey,
    scopeKey,
    operation: "SETTLE_CUSTOMER_RECEIVABLES_ALL_V1",
    payload,
    state: "PENDING",
    attempts: 0,
    nextAttemptAt: null,
    lastError: null,
    createdAt: localCommittedAt,
    updatedAt: localCommittedAt,
  };

  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({
      localId: batchLocalId(localBatchId),
      scopeKey,
      kind: "RECEIVABLE_SETTLEMENT_ALL",
      state: "PENDING_SERVER_CONFIRMATION",
      requestKey: rootRequestKey,
      snapshot,
      createdAt: localCommittedAt,
      updatedAt: localCommittedAt,
    });
    await posRuntimeDb.outbox.put(outbox);
  });

  return snapshot;
}

function isRetryableGatewayError(error: string) {
  return error === "GATEWAY_TIMEOUT"
    || error === "GATEWAY_UNREACHABLE"
    || error === "RECEIVABLE_SETTLEMENT_ALL_FAILED"
    || error.startsWith("GATEWAY_HTTP_5");
}

async function markRetryableFailure(record: OutboxRecord, message: string) {
  const now = new Date().toISOString();
  await posRuntimeDb.outbox.put({
    ...record,
    state: "FAILED",
    attempts: record.attempts + 1,
    nextAttemptAt: new Date(Date.now() + 5_000).toISOString(),
    lastError: message,
    updatedAt: now,
  });
}

async function markBusinessRejected(record: OutboxRecord, journal: JournalRecord, message: string) {
  const now = new Date().toISOString();
  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({ ...journal, state: "REJECTED", updatedAt: now });
    await posRuntimeDb.outbox.put({
      ...record,
      state: "ACKED",
      attempts: record.attempts + 1,
      nextAttemptAt: null,
      lastError: message,
      updatedAt: now,
    });
  });
}

function latestBalanceChangedAt(result: ReceivableSettlementAllResult) {
  const timestamps = (result.receivable_balances ?? [])
    .map((balance) => balance.updated_at)
    .filter((value): value is string => Boolean(value));
  if (!timestamps.length) return new Date().toISOString();
  return timestamps.reduce((latest, value) => (
    Date.parse(value) > Date.parse(latest) ? value : latest
  ));
}

export async function syncReceivableSettlementAllOutbox(
  identity: TrustedTerminalIdentity,
  deviceSecret: string,
  sessionToken: string,
  requestKey: string,
) {
  const record = await posRuntimeDb.outbox.get(requestKey);
  if (!record || record.operation !== "SETTLE_CUSTOMER_RECEIVABLES_ALL_V1") {
    throw new Error("RECEIVABLE_SETTLEMENT_ALL_OUTBOX_NOT_FOUND");
  }
  const journal = await posRuntimeDb.journal.where("requestKey").equals(requestKey).first();
  if (!journal || journal.kind !== "RECEIVABLE_SETTLEMENT_ALL") throw new Error("RECEIVABLE_SETTLEMENT_ALL_JOURNAL_NOT_FOUND");
  const snapshot = snapshotFromJournal(journal);
  if (!snapshot) throw new Error("RECEIVABLE_SETTLEMENT_ALL_SNAPSHOT_INVALID");
  if (record.state === "ACKED" && journal.state === "CONFIRMED" && snapshot.canonical) return snapshot;

  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) throw new Error("POS_GATEWAY_NOT_CONFIGURED");
  const payload = record.payload as ReceivableSettlementAllOutboxPayload;
  await posRuntimeDb.outbox.put({ ...record, state: "SENDING", updatedAt: new Date().toISOString() });

  const client = new PosReceivableSettlementAllGatewayClient({ baseUrl });
  const result = await client.settleAll({
    deviceSecret,
    sessionToken,
    customerId: payload.customerId,
    receivables: payload.receivables,
    expectedTotal: payload.expectedTotal,
    paymentMethodCode: payload.paymentMethodCode,
    receivingDestinationId: payload.receivingDestinationId,
    tenderedAmount: payload.tenderedAmount,
    localChangeAmount: payload.localChangeAmount,
    verificationSource: payload.verificationSource,
    requestKey,
    clientContext: {
      runtime: "WEBPOS",
      terminal_node_id: identity.node_id,
      local_batch_id: snapshot.localBatchId,
      local_committed_at: payload.localCommittedAt,
      local_commit_authority: false,
    },
  });

  if (!result.ok || !result.data) {
    const error = result.error ?? "RECEIVABLE_SETTLEMENT_ALL_FAILED";
    console.error("[receivable-settlement-all]", {
      requestKey,
      customerId: payload.customerId,
      receivableCount: payload.receivables.length,
      error,
      reasonCode: result.reasonCode,
    });
    if (isRetryableGatewayError(error)) await markRetryableFailure(record, error);
    else await markBusinessRejected(record, journal, error);
    throw new Error(error);
  }

  await applyCanonicalCustomerCreditSummaryReadback(
    identity,
    result.data.customer_credit_summary,
    latestBalanceChangedAt(result.data),
  );

  const ackedAt = new Date().toISOString();
  const confirmed: LocalReceivableSettlementAllSnapshot = { ...snapshot, canonical: result.data };
  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({ ...journal, state: "CONFIRMED", snapshot: confirmed, updatedAt: ackedAt });
    await posRuntimeDb.outbox.put({
      ...record,
      state: "ACKED",
      attempts: record.attempts + 1,
      nextAttemptAt: null,
      lastError: null,
      updatedAt: ackedAt,
    });
  });
  await clearSettledCustomerMemberContext(identity, snapshot.customerId);
  return confirmed;
}

export async function syncPendingReceivableSettlementAll(
  identity: TrustedTerminalIdentity,
  deviceSecret: string,
  sessionToken: string,
) {
  const pending = await posRuntimeDb.outbox
    .where("operation")
    .equals("SETTLE_CUSTOMER_RECEIVABLES_ALL_V1")
    .filter((record) => record.state !== "ACKED")
    .sortBy("createdAt");
  const results: Array<{ requestKey: string; ok: boolean; error?: string }> = [];
  for (const record of pending) {
    if (record.scopeKey !== settlementScope(identity)) continue;
    if (record.nextAttemptAt && Date.parse(record.nextAttemptAt) > Date.now()) continue;
    try {
      await syncReceivableSettlementAllOutbox(identity, deviceSecret, sessionToken, record.requestKey);
      results.push({ requestKey: record.requestKey, ok: true });
    } catch (error) {
      results.push({
        requestKey: record.requestKey,
        ok: false,
        error: error instanceof Error ? error.message : "RECEIVABLE_SETTLEMENT_ALL_FAILED",
      });
    }
  }
  return results;
}
